// Rutas de la API y la forma de cada cuerpo. El servidor valida CADA cuerpo con estos esquemas (strict: un campo de
// más es error 400) y la interfaz los usa para construir las peticiones. Toda respuesta de error es ErrorApi
// (errores.ts). Todo lo que cambia una cotización manda `revision` (concurrencia optimista → 409 CONFLICTO).
import { z } from 'zod';
import { Correo, Fecha, Id, Medidor, Texto } from './dominio/basicos.ts';
import { Cuestionario, MENSAJE_AISLADO_SOLO_CASA, MENSAJE_FALTA_REGIMEN, aisladoDeNegocio, faltaRegimen } from './dominio/cuestionario.ts';
import type { Carga } from './dominio/cuestionario.ts';
import { MAX_DESCRIPCION } from './estimar-consumo.ts';
import { ClaseArchivo } from './dominio/archivos.ts';
import { EdicionCampo } from './dominio/edicion.ts';
import { CuerpoCosto } from './dominio/costos.ts';
import { EstadoCotizacion } from './dominio/cotizacion.ts';
import { CuerpoVenta } from './dominio/lista-compra.ts';
import { PiezasAmpliacion } from './dominio/ampliacion-baterias.ts';
import type { Cotizacion, DetalleCotizacion, Evento, ResumenCotizacion, ResumenVersion, VersionCotizacion } from './dominio/cotizacion.ts';
import type { Usuario, UsuarioSesion } from './dominio/usuario.ts';
import type { ConfigPublica } from './dominio/config.ts';
import type { EstadoIA, ResultadoCorregir, UsoIA } from './dominio/ia.ts';
import { CascadaIA, EntradaSugerirConsumos, ModeloIA } from './dominio/ia.ts';
import { EVENTOS_POR_PAGINA_MAX } from './constantes.ts';
import type { TablaPrecios, VistaPrecios } from './dominio/precios.ts';
import type { ArchivoSubido } from './dominio/archivos.ts';
import type { EntradaBitacora } from './dominio/bitacora.ts';

type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export interface Ruta {
  metodo: Metodo;
  ruta: string;
  /** Quién puede llamarla: "publica" sin sesión; el resto con sesión y el permiso dicho (usuario.ts → puede()). */
  permiso:
    | 'publica'
    | 'sesion'
    | 'editar_cotizaciones'
    | 'capturar_costos'
    | 'editar_precios'
    | 'administrar_usuarios'
    | 'configurar_ia'
    | 'configurar_crm'
    | 'ver_bitacora'
    | 'aprobar_precios'
    | 'autorizar_descuentos'
    | 'administrar_papelera'
    /** ADENDA A0b / z59: solo Diego (costos, márgenes y la lista de compra). */
    | 'ver_costos';
}

export const RUTAS = {
  salud: { metodo: 'GET', ruta: '/api/salud', permiso: 'publica' },

  // Acceso con enlace al correo
  pedirEnlace: { metodo: 'POST', ruta: '/api/acceso/enlace', permiso: 'publica' },
  /** GET con ?token=…: SIN &confirmar=si responde una página con un botón «Entrar» (los filtros de correo de Microsoft 365
   *  abren los enlaces para revisarlos: si con solo abrirlo se gastara, a la persona le llegaría "ya usado"). Con
   *  &confirmar=si consume el enlace, pone la cookie de sesión y redirige a "/" (o a "/entrar?error=enlace" si no sirve).
   *  En modo desarrollo el enlace ya trae &confirmar=si. */
  entrar: { metodo: 'GET', ruta: '/api/acceso/entrar', permiso: 'publica' },
  entrarFirebase: { metodo: 'POST', ruta: '/api/acceso/firebase', permiso: 'publica' },
  /** Responde { ok: true }. */
  salir: { metodo: 'POST', ruta: '/api/acceso/salir', permiso: 'sesion' },
  yo: { metodo: 'GET', ruta: '/api/yo', permiso: 'sesion' },

  // Usuarios (todos los ven para filtrar por vendedor; solo el director los cambia)
  listarUsuarios: { metodo: 'GET', ruta: '/api/usuarios', permiso: 'sesion' },
  crearUsuario: { metodo: 'POST', ruta: '/api/usuarios', permiso: 'administrar_usuarios' },
  editarUsuario: { metodo: 'PATCH', ruta: '/api/usuarios/:id', permiso: 'administrar_usuarios' },

  // Cotizaciones y mini CRM
  listarCotizaciones: { metodo: 'GET', ruta: '/api/cotizaciones', permiso: 'sesion' },
  crearCotizacion: { metodo: 'POST', ruta: '/api/cotizaciones', permiso: 'editar_cotizaciones' },
  detalleCotizacion: { metodo: 'GET', ruta: '/api/cotizaciones/:id', permiso: 'sesion' },
  /** Nube, tanda 2 · historial paginado: query ConsultaEventos (`antes` = el cursor `eventos_antes` del detalle o de la
   *  página anterior). Responde RespuestaEventos (del más viejo al más nuevo). Pasa por la privacidad A0b como el detalle. */
  historialCotizacion: { metodo: 'GET', ruta: '/api/cotizaciones/:id/eventos', permiso: 'sesion' },
  editarBorrador: { metodo: 'PATCH', ruta: '/api/cotizaciones/:id/borrador', permiso: 'editar_cotizaciones' },
  actualizarCrm: { metodo: 'PATCH', ruta: '/api/cotizaciones/:id', permiso: 'editar_cotizaciones' },
  /** multipart/form-data: campo "archivo" + campos "medidor" (A|B) y "clase" (recibo|finsus|foto_sitio|otro) + "revision". */
  subirArchivo: { metodo: 'POST', ruta: '/api/cotizaciones/:id/archivos', permiso: 'capturar_costos' },
  quitarArchivo: { metodo: 'DELETE', ruta: '/api/cotizaciones/:id/archivos/:archivoId', permiso: 'editar_cotizaciones' },
  descargarArchivo: { metodo: 'GET', ruta: '/api/archivos/:archivoId', permiso: 'sesion' },
  leerRecibos: { metodo: 'POST', ruta: '/api/cotizaciones/:id/leer', permiso: 'editar_cotizaciones' },
  /** Etapa 4 (Diego, 12-oct): "después de leer, el vendedor revisa, autoriza o corrige la tabla antes de calcular".
   *  Cuerpo CuerpoAutorizarLectura (las correcciones van en `ediciones`, solo bajo /lecturas/:medidor). Responde
   *  RespuestaCotizacion, ya calculada con la tabla autorizada. Queda en el historial quién la autorizó. */
  autorizarLectura: { metodo: 'POST', ruta: '/api/cotizaciones/:id/lecturas/:medidor/autorizar', permiso: 'editar_cotizaciones' },
  corregirConIA: { metodo: 'POST', ruta: '/api/cotizaciones/:id/corregir', permiso: 'editar_cotizaciones' },
  /** hoy86 · ADENDA z64-2: «Sugerir con IA» de la Revisión en un negocio PDBT (sugerirUsaIA): la IA estima los periodos del año sin
   *  recibo de ESE cliente. Cuerpo CuerpoSugerirConsumos (la ciudad la pone el servidor de la cotización). Responde
   *  ResultadoSugerirConsumos. No guarda nada: la web pone lo sugerido en la tabla marcado «estimado» y el vendedor autoriza. Si la
   *  IA no sirve (sin llave, tope, rechazo, cifras raras) responde fuente «curva» con el motivo y la web pone la curva con aviso. */
  sugerirConsumos: { metodo: 'POST', ruta: '/api/cotizaciones/:id/sugerir-consumos', permiso: 'editar_cotizaciones' },
  /** z11 (Diego, 14/16-oct): cliente NUEVO de casa o negocio. El vendedor describe la casa o el negocio y sus equipos y la
   *  IA de «corregir» (z25, el modelo de Configuración → IA) propone el cuadro de cargas. Cuerpo CuerpoEstimarConsumo.
   *  Responde RespuestaEstimarConsumo. No guarda nada: el vendedor corrige la tabla y la cotización se crea como siempre. */
  estimarConsumo: { metodo: 'POST', ruta: '/api/estimar-consumo', permiso: 'editar_cotizaciones' },
  agregarCosto: { metodo: 'POST', ruta: '/api/cotizaciones/:id/costos', permiso: 'capturar_costos' },
  quitarCosto: { metodo: 'DELETE', ruta: '/api/cotizaciones/:id/costos/:costoId', permiso: 'capturar_costos' },
  calcular: { metodo: 'POST', ruta: '/api/cotizaciones/:id/calcular', permiso: 'editar_cotizaciones' },
  /** OPTIMUS (Diego, 12-oct noche): «Aplicar el precio de…»: pone a la cotización el precio que le toca HOY a su vendedor
   *  (con el factor de Optimus si es de Optimus, sin él si no) y recalcula. Es la ÚNICA forma de cambiar
   *  parametros.precio_optimus (una cotización ya hecha no cambia de precio sola). Cuerpo CuerpoRevision. Responde
   *  RespuestaCotizacion; el historial dice el contado de antes y de después. */
  aplicarPrecioVendedor: { metodo: 'POST', ruta: '/api/cotizaciones/:id/precio-optimus', permiso: 'editar_cotizaciones' },
  revisarConIA: { metodo: 'POST', ruta: '/api/cotizaciones/:id/revisar', permiso: 'editar_cotizaciones' },
  /** ADENDA z48-8 (Diego, 28-oct): «Actualizar a la versión nueva» (dominio/version.ts). Cuerpo CuerpoActualizarVersion:
   *  `confirmar: false` calcula EN SECO con los parámetros de versión que le faltan y no guarda nada; `true` guarda, deja el
   *  evento en el historial y el PDF anterior queda viejo. Responde RespuestaActualizarVersion (con `cotizacion` al confirmar). */
  actualizarVersion: { metodo: 'POST', ruta: '/api/cotizaciones/:id/version-nueva', permiso: 'editar_cotizaciones' },
  resolverAviso: { metodo: 'POST', ruta: '/api/cotizaciones/:id/avisos/:avisoId/resolver', permiso: 'editar_cotizaciones' },
  guardarVersion: { metodo: 'POST', ruta: '/api/cotizaciones/:id/versiones', permiso: 'editar_cotizaciones' },
  verVersion: { metodo: 'GET', ruta: '/api/cotizaciones/:id/versiones/:numero', permiso: 'sesion' },
  generarPdf: { metodo: 'POST', ruta: '/api/cotizaciones/:id/pdf', permiso: 'editar_cotizaciones' },
  /** Diego, 13-oct: el PDF de TODAS las sucursales del proyecto (formato multisitio), guardado como versión de la sucursal
   *  principal (folio más bajo). Sin cuerpo. Responde RespuestaVersion (de la principal). 422 PDF_FALLO dice qué sucursal
   *  falta calcular o revisar. */
  generarPdfSucursales: { metodo: 'POST', ruta: '/api/cotizaciones/:id/pdf-sucursales', permiso: 'editar_cotizaciones' },
  /** MULTISITIO · la franja de sucursales: las sucursales del proyecto de esta cotización (sin las de la papelera), por
   *  folio. Responde RespuestaSucursales. 400 si es de un solo sitio. NO es una lista del CRM: lee solo ese proyecto, así
   *  que con el fusible de listas saltado (Datastore) sigue respondiendo, como abrir una cotización. Pasa por la
   *  privacidad A0b como el detalle; de lo ajeno, el externo no ve las notas (como en la lista). */
  sucursalesProyecto: { metodo: 'GET', ruta: '/api/cotizaciones/:id/sucursales', permiso: 'sesion' },
  /** ADENDA z65 (30-oct-2026) · «Separar en otro sitio»: los recibos de otro número de servicio pasan a un sitio nuevo del
   *  mismo proyecto (la cotización se vuelve multisitio si no lo era). Cuerpo y candados en apps/servidor/src/dominio/sitios-proyecto.ts.
   *  Responde { original, nueva }. */
  separarSitio: { metodo: 'POST', ruta: '/api/cotizaciones/:id/separar-sitio', permiso: 'editar_cotizaciones' },
  /** ADENDA A0b · el vendedor pide el OK de Diego para el precio (descuento > tope, margen < mínimo, renglón sin
   *  monto). Cuerpo CuerpoPedirAprobacion. Responde RespuestaAprobacion (con el enlace de WhatsApp a Diego). */
  pedirAprobacion: { metodo: 'POST', ruta: '/api/cotizaciones/:id/aprobacion', permiso: 'editar_cotizaciones' },
  /** ADENDA A0b · Diego aprueba o niega el precio que está viendo. Cuerpo CuerpoResolverAprobacion. Responde
   *  RespuestaCotizacion. Director, o un asesor interno si es solo un descuento y la cotización no es suya
   *  (motivoNoAutoriza, Diego 11/12-oct); queda en el historial y en la bitácora. */
  resolverAprobacion: { metodo: 'POST', ruta: '/api/cotizaciones/:id/aprobacion/resolver', permiso: 'autorizar_descuentos' },
  /** PAPELERA (Diego, 13-oct): manda la cotización a la papelera (recuperable). Cuerpo CuerpoRevision. Responde
   *  RespuestaCotizacion. Diego borra cualquiera; un vendedor solo las suyas en Borrador (motivoNoMandaAPapelera → 403).
   *  Queda en el historial de la cotización y en la bitácora. */
  mandarAPapelera: { metodo: 'POST', ruta: '/api/cotizaciones/:id/papelera', permiso: 'editar_cotizaciones' },
  /** PAPELERA: Diego la saca de la papelera y vuelve como estaba. Cuerpo CuerpoRevision. Responde RespuestaCotizacion. */
  recuperarCotizacion: { metodo: 'POST', ruta: '/api/cotizaciones/:id/recuperar', permiso: 'administrar_papelera' },
  /** ADENDA z59 (P1–P4): la LISTA DE COMPRA congelada al marcar la venta, para reenviársela a Hiram. SOLO Diego (`ver_costos`):
   *  a cualquier otro, 403. Query ConsultaListaCompra: `version=hiram` (sin precio de venta ni margen, P2) o `diego` (con el techo
   *  que mantiene el verde); `formato=html` (la hoja, para bajar e imprimir o reenviar) o `sobre` (el bloque «costeo» del sobre v2
   *  del CRM, JSON). 409 si la cotización no está Ganada o su lista no se armó; 500 LISTA_NO_CUADRA si la aserción truena. Si la lista
   *  del mes cambió más de 3 % contra la congelada, la hoja lo marca arriba para que decida Diego. */
  listaDeCompra: { metodo: 'GET', ruta: '/api/cotizaciones/:id/lista-compra', permiso: 'ver_costos' },
  /** Responde RespuestaCotizacion. Hoy siempre 409 CRM_EXTERNO_APAGADO (el CRM de Carlos está apagado). */
  enviarCrmExterno: { metodo: 'POST', ruta: '/api/cotizaciones/:id/crm-externo', permiso: 'editar_cotizaciones' },

  // Precios (todos los ven; solo el director los cambia)
  verPrecios: { metodo: 'GET', ruta: '/api/precios', permiso: 'sesion' },
  guardarPrecios: { metodo: 'PUT', ruta: '/api/precios', permiso: 'editar_precios' },

  // Configuración de la IA (solo director). La llave entra por aquí y NUNCA vuelve a salir.
  estadoIA: { metodo: 'GET', ruta: '/api/config/ia', permiso: 'configurar_ia' },
  guardarLlaveIA: { metodo: 'PUT', ruta: '/api/config/ia/llave', permiso: 'configurar_ia' },
  borrarLlaveIA: { metodo: 'DELETE', ruta: '/api/config/ia/llave', permiso: 'configurar_ia' },
  guardarTopeIA: { metodo: 'PUT', ruta: '/api/config/ia/tope', permiso: 'configurar_ia' },
  /** Contrato v2. Cuerpo CuerpoModeloIA ({ modelo: id de MODELOS_IA | null = volver a MODELO_IA }). Responde
   *  RespuestaEstadoIA. Lo elegido en pantalla manda sobre IA_MODELO de .env (como el tope). Queda en la bitácora
   *  ("ia_modelo"). */
  guardarModeloIA: { metodo: 'PUT', ruta: '/api/config/ia/modelo', permiso: 'configurar_ia' },
  /** 12-oct · cascada de la lectura de recibos (apagada por omisión). Cuerpo CuerpoCascadaIA. Responde RespuestaEstadoIA.
   *  Queda en la bitácora ("ia_modelo"). */
  guardarCascadaIA: { metodo: 'PUT', ruta: '/api/config/ia/cascada', permiso: 'configurar_ia' },

  // Bitácora del sistema (contrato v2, dominio/bitacora.ts). Query: ConsultaBitacora. Responde RespuestaBitacora. Solo
  // director. El historial de CADA cotización sigue siendo DetalleCotizacion.eventos.
  verBitacora: { metodo: 'GET', ruta: '/api/bitacora', permiso: 'ver_bitacora' },

  // «Hoy toca» (Diego, 2-nov): a quién le escribe cada asesor hoy, y qué pasó. Query ConsultaHoyToca: un vendedor solo pide lo suyo; Diego, cualquiera o «todos».
  hoyToca: { metodo: 'GET', ruta: '/api/seguimiento/hoy', permiso: 'editar_cotizaciones' },
  tableroSeguimiento: { metodo: 'GET', ruta: '/api/seguimiento/tablero', permiso: 'ver_costos' },
  mensajeSeguimiento: { metodo: 'POST', ruta: '/api/cotizaciones/:id/seguimiento/mensaje', permiso: 'editar_cotizaciones' },
  resultadoSeguimiento: { metodo: 'POST', ruta: '/api/cotizaciones/:id/seguimiento/resultado', permiso: 'editar_cotizaciones' },
} as const satisfies Record<string, Ruta>;
export type NombreRuta = keyof typeof RUTAS;

/** Rutas YA DEFINIDAS (método, ruta, permiso, cuerpo y respuesta) que el servidor todavía no implementa. NO van en
 *  RUTAS mientras tanto, a propósito: el servidor se niega a arrancar si una ruta de RUTAS no está implementada
 *  (candado bueno), así que meterlas antes rompería el arranque. El servidor las da de alta desde AQUÍ; el integrador
 *  las MUEVE a RUTAS en cuanto existan (desde ese momento el candado de "todas implementadas" las cubre) y deja este
 *  objeto vacío. Ninguna repite nombre ni (método, ruta) de RUTAS (lo revisa la prueba de compartido).
 *  Vacío desde la integración del 10-oct-2026: verBitacora y guardarModeloIA ya están en RUTAS. */
export const RUTAS_PREPARADAS = {} as const satisfies Record<string, Ruta>;
export type NombreRutaPreparada = keyof typeof RUTAS_PREPARADAS;

const TODAS_LAS_RUTAS: Readonly<Record<NombreRuta | NombreRutaPreparada, Ruta>> = Object.freeze({ ...RUTAS, ...RUTAS_PREPARADAS });

/** "/api/cotizaciones/:id" + {id: "abc"} → "/api/cotizaciones/abc". Truena si falta un parámetro. Acepta también las
 *  rutas preparadas (contrato v2). */
export function rutaCon(nombre: NombreRuta | NombreRutaPreparada, params: Record<string, string | number> = {}): string {
  return TODAS_LAS_RUTAS[nombre].ruta.replace(/:([A-Za-z]+)/g, (_, k: string) => {
    const v = params[k];
    if (v === undefined) throw new Error(`rutaCon(${nombre}): falta el parámetro ${k}`);
    return encodeURIComponent(String(v));
  });
}

// ───────────────────────────── Cuerpos (entrada) ─────────────────────────────

export const CuerpoPedirEnlace = z.strictObject({ correo: Correo });
export const CuerpoEntrarFirebase = z.strictObject({ id_token: z.string().min(20).max(5_000) });

export const ConsultaCotizaciones = z.strictObject({
  estado: EstadoCotizacion.optional(),
  vendedor_id: Id.optional(),
  texto: z.string().trim().max(80).optional(),
  desde: Fecha.optional(),
  hasta: Fecha.optional(),
  limite: z.coerce.number().int().min(1).max(500).optional(),
  /** "si" = solo las enviadas que llevan DIAS_SIN_RESPUESTA días o más sin cambio de estado (ADENDA A0b). */
  sin_respuesta: z.enum(['si']).optional(),
  /** PAPELERA (Diego, 13-oct): "si" = SOLO las que están en la papelera (solo Diego: 403 a los demás). Sin él, la lista
   *  NUNCA trae las de la papelera. */
  papelera: z.enum(['si']).optional(),
});

/** Nube, tanda 2 · historialCotizacion: los eventos ANTERIORES al cursor `antes` (null/ausente = los últimos). */
export const ConsultaEventos = z.strictObject({
  antes: z.coerce.number().int().min(1).optional(),
  limite: z.coerce.number().int().min(1).max(EVENTOS_POR_PAGINA_MAX).optional(),
});

export const CuerpoCrearCotizacion = z
  .strictObject({
    cuestionario: Cuestionario,
    /** null = la persona que la crea. */
    vendedor_id: Id.nullable(),
    /** MULTISITIO: esta cotización es una sucursal. `junto_a` = otra sucursal ya creada del mismo proyecto (el servidor
     *  copia su grupo); null = la primera sucursal (el servidor abre el grupo). Ausente = un solo sitio, como siempre. */
    multisitio: z.strictObject({ sitio: Texto(2, 40), junto_a: Id.nullable() }).optional(),
    /** Ampliación de baterías (27-oct-2026): la captura con que nace (la batería, cuántas tiene y cuántas suma; lo demás se
     *  captura después en su pantalla). Solo con cuestionario.tipo «ampliacion_baterias»; ausente = vacía. */
    piezas: PiezasAmpliacion.optional(),
  })
  // Revisión de ventas, propuesta 5 (Diego, 12-oct noche): una cotización NUEVA de comercio o industria no se crea sin el
  // régimen fiscal (o «No sé todavía»). El esquema del cuestionario lo deja opcional para que las de antes sigan abriendo.
  .superRefine((b, ctx) => {
    if (faltaRegimen(b.cuestionario)) ctx.addIssue({ code: 'custom', path: ['cuestionario', 'fiscal'], message: MENSAJE_FALTA_REGIMEN });
    // ADENDA z44/z45 (Diego, 27-oct): el aislado es solo para residencial (el de negocio ya guardado sigue abriendo)
    if (aisladoDeNegocio(b.cuestionario)) ctx.addIssue({ code: 'custom', path: ['cuestionario', 'cliente', 'tipo'], message: MENSAJE_AISLADO_SOLO_CASA });
    if (b.piezas && b.cuestionario.tipo !== 'ampliacion_baterias') ctx.addIssue({ code: 'custom', path: ['piezas'], message: 'La captura de piezas solo va en una ampliación de baterías.' });
    if (b.multisitio && b.cuestionario.tipo === 'ampliacion_baterias') ctx.addIssue({ code: 'custom', path: ['multisitio'], message: 'Una ampliación de baterías no es de varias sucursales.' });
  });

export const CuerpoEditarBorrador = z.strictObject({ revision: z.int().nonnegative(), ediciones: z.array(EdicionCampo).min(1).max(200) });

export const CuerpoActualizarCrm = z
  .strictObject({
    revision: z.int().nonnegative(),
    estado: EstadoCotizacion.optional(),
    vendedor_id: Id.optional(),
    notas_crm: z.string().trim().max(2_000).nullable().optional(),
    proximo_seguimiento: Fecha.nullable().optional(),
    /** ADENDA z59 (C5): al pasar a «Ganada», QUÉ se vendió (la opción y, en una propuesta de baterías, el nivel). El servidor la
     *  exige (422) cuando la cotización tiene opciones: nunca toma la recomendada en silencio. Solo va junto con `estado: 'ganada'`. */
    venta: CuerpoVenta.optional(),
  })
  .refine((c) => Object.keys(c).length > 1, { message: 'No hay nada que cambiar.' })
  .refine((c) => c.venta === undefined || c.estado === 'ganada', { message: 'La venta (qué opción se vendió) solo va al marcar la cotización Ganada.', path: ['venta'] });

/** Campos de texto del multipart de subirArchivo (el archivo va aparte). */
export const CamposSubirArchivo = z.strictObject({
  revision: z.coerce.number().int().nonnegative(),
  medidor: Medidor,
  clase: ClaseArchivo.extract(['recibo', 'finsus', 'foto_sitio', 'otro']),
});

export const CuerpoRevision = z.strictObject({ revision: z.int().nonnegative() });
/** ADENDA z59: la hoja de la lista de compra (la de Hiram por omisión) y en qué forma. */
export const ConsultaListaCompra = z.strictObject({ version: z.enum(['hiram', 'diego']).default('hiram'), formato: z.enum(['html', 'sobre']).default('html'), descargar: z.enum(['si']).optional() });
/** Etapa 4: autorizar la tabla leída de un medidor, con las correcciones del vendedor (si hay) en el mismo paso. */
export const CuerpoAutorizarLectura = z.strictObject({ revision: z.int().nonnegative(), ediciones: z.array(EdicionCampo).max(200).default([]) });
export const ConsultaRevision = z.strictObject({ revision: z.coerce.number().int().nonnegative() });

export const CuerpoCorregir = z.strictObject({ revision: z.int().nonnegative(), instruccion: Texto(3, 1_000) });
/** hoy86 · z64-2: lo que la tabla de Revisión le pide a la IA (compartido: cuerpoSugerir). */
export const CuerpoSugerirConsumos = EntradaSugerirConsumos.omit({ ciudad: true });
export const CuerpoEstimarConsumo = z.strictObject({
  descripcion: Texto(3, MAX_DESCRIPCION),
  tipo: z.enum(['casa', 'comercio']),
  /** ADENDA z52-2: 'aumento' = cuánto SUBE el consumo de quien ya tiene recibo (solo los equipos nuevos). Ausente = 'nuevo' (z11). */
  modo: z.enum(['nuevo', 'aumento']).default('nuevo'),
  /** El giro del negocio (la IA ajusta las horas al día). */
  giro: z.string().trim().max(120).nullable().optional(),
  /** Con modo 'aumento': la cotización a cuya bitácora va el uso y el costo de la IA (el vendedor tiene que poder editarla). */
  cotizacion_id: Id.optional(),
});
export const CuerpoAgregarCosto = z.strictObject({ revision: z.int().nonnegative(), costo: CuerpoCosto });
/** La nota es opcional (Diego, junta del 13-oct): sin ella el aviso queda «Visto (sin nota)». */
export const CuerpoResolverAviso = z.strictObject({ revision: z.int().nonnegative(), nota: Texto(3, 500).optional() });
export const CuerpoGuardarVersion = z.strictObject({ revision: z.int().nonnegative(), motivo: Texto(2, 300) });

export const CuerpoGenerarPdf = z.strictObject({
  revision: z.int().nonnegative(),
  motivo: Texto(2, 300),
  /** Nota OPCIONAL por aviso rojo (Diego, junta del 13-oct): el que no la trae sale anotado como sugerencia vista; solo Optimus detiene. */
  confirmar_avisos_rojos: z.array(z.strictObject({ aviso_id: Id, nota: Texto(3, 500) })).max(50),
});

/** ADENDA A0b · pedir el OK de Diego. `nota`: lo que el vendedor le quiere decir ("el cliente compara con otra marca"). */
export const CuerpoPedirAprobacion = z.strictObject({ revision: z.int().nonnegative(), nota: z.string().trim().max(500).nullable() });
/** ADENDA A0b · Diego aprueba (true) o niega (false) los precios que ve en la revisión `revision`. */
export const CuerpoResolverAprobacion = z.strictObject({ revision: z.int().nonnegative(), aprobar: z.boolean(), nota: z.string().trim().max(500).nullable() });

export const CuerpoLlaveIA = z.strictObject({
  llave: z.string().trim().regex(/^sk-ant-[A-Za-z0-9_-]{20,}$/, 'La llave de Anthropic empieza con "sk-ant-".'),
});
export const CuerpoTopeIA = z.strictObject({ tope_mes_usd: z.number().positive().max(10_000).nullable() });
/** Contrato v2 · RUTAS_PREPARADAS.guardarModeloIA. null = volver al modelo por omisión (MODELO_IA, o IA_MODELO de .env). */
export const CuerpoModeloIA = z.strictObject({ modelo: ModeloIA.nullable() });
export const CuerpoCascadaIA = CascadaIA;

// ───────────────────────────── Respuestas (salida) ─────────────────────────────

export interface RespuestaSalud {
  ok: true;
  version: string;
  ahora: string;
  /** Nube, tanda 1 (HOSTING-RECOMENDACION §3.3.7): qué base y qué almacén usa ESTE servidor. Se MIDE aquí; un documento
   *  que diga qué está desplegado caduca al día siguiente. */
  base: 'local' | 'datastore';
  archivos: 'local' | 'nube';
  /** true = lo capturado se BORRA cuando la instancia se apaga (Cloud Run con BASE/ARCHIVOS=local y ENSAYO_EFIMERO=si). */
  efimero: boolean;
  /** Corre en Cloud Run (existe K_SERVICE) o desde la imagen de la nube (existe COMMIT_DESPLEGADO). */
  nube: boolean;
  /** Commit de git con el que se construyó la imagen (COMMIT_DESPLEGADO). null fuera de la nube. */
  commit: string | null;
  /** Huella de la copia de la plantilla de PDF que va dentro de la imagen (PLANTILLA.json). null si no hay copia. */
  plantilla: string | null;
  /** Nube, tanda 2 · fusible de lecturas de Datastore (el único gasto de Google sin tope): en qué estado está HOY (fecha de
   *  Hermosillo). Pública: solo los estados, nunca la cuenta ni el umbral (hallazgo del 12-oct: un atacante veía cuánto le
   *  faltaba). La cuenta completa va en /api/yo, con sesión. null con BASE=local (SQLite no cobra lecturas). */
  lecturas: EstadoFusiblePublico | null;
  /** Solo con ?proxy=si (para medir en el ensayo el formato de X-Forwarded-For): lo que el servidor cree que es la IP
   *  de quien pregunta (es SU PROPIA IP: no se filtra nada de nadie más) y cuántas entradas trajo el encabezado. */
  proxy?: { confiar: string; ip_vista: string; entradas_xff: number };
}
/** Nube, tanda 2 · lo que mide el fusible de lecturas (ConfigPublica.almacenamiento.lecturas, con sesión).
 *  Las lecturas de peticiones SIN sesión (pedir enlace, entrar) se cuentan aparte (`anonimas`) y tienen su propio tope:
 *  un anónimo nunca detiene las listas ni la base de quien ya entró (hallazgo del 12-oct).
 *   - `saltado`: las lecturas de quien tiene sesión pasaron el umbral → la lista y la búsqueda dejan de responder.
 *   - `detenido`: pasaron 3 veces el umbral → la base deja de responder (entrar y ver la franja sí).
 *   - `entradas_cerradas`: las anónimas pasaron su tope (un décimo del umbral) → nadie NUEVO entra hasta mañana.
 *   - `apagado`: el total pasó 4 veces el umbral → todo se detiene, incluso entrar (el techo duro del día). */
export interface LecturasBase {
  fecha: string;
  hoy: number;
  anonimas: number;
  umbral: number;
  saltado: boolean;
  detenido: boolean;
  entradas_cerradas: boolean;
  apagado: boolean;
}
/** Lo que /api/salud (pública) dice del fusible: solo los estados. */
export type EstadoFusiblePublico = Pick<LecturasBase, 'fecha' | 'saltado' | 'detenido' | 'entradas_cerradas' | 'apagado'>;
export interface RespuestaPedirEnlace {
  /** Siempre el mismo texto, esté o no dado de alta el correo. */
  mensaje: string;
  modo_desarrollo: boolean;
  /** Solo en modo desarrollo y solo si el correo está dado de alta. */
  enlace_desarrollo: string | null;
}
export interface RespuestaYo {
  usuario: UsuarioSesion;
  config: ConfigPublica;
}
export interface RespuestaUsuarios {
  usuarios: Usuario[];
  /** Punto o (Diego, 13-oct, 19:05): ids de los usuarios SIN tarjeta en la plantilla del PDF y SIN WhatsApp: una cotización a
   *  su nombre no saca PDF hasta que Diego ponga su WhatsApp («Revisa antes de crear» lo advierte). Ausente = no se sabe. */
  sin_whatsapp_ni_tarjeta?: string[];
}
export interface RespuestaUsuario {
  usuario: Usuario;
}
export interface RespuestaListaCotizaciones {
  cotizaciones: ResumenCotizacion[];
}
/** GET /api/cotizaciones/:id/sucursales: las sucursales del proyecto, por folio (sin las de la papelera). */
export interface RespuestaSucursales {
  cotizaciones: ResumenCotizacion[];
}
export interface RespuestaCotizacion {
  cotizacion: Cotizacion;
}
export type RespuestaDetalle = DetalleCotizacion;
/** Nube, tanda 2 · historialCotizacion: una página del historial (del más viejo al más nuevo) y el cursor para la anterior
 *  (null = ya no hay más). */
export interface RespuestaEventos {
  eventos: Evento[];
  antes: number | null;
}
export interface RespuestaArchivo {
  archivo: ArchivoSubido;
  cotizacion: Cotizacion;
}
export interface RespuestaConUso {
  cotizacion: Cotizacion;
  uso: UsoIA;
}
/** z11: el cuadro de cargas que propuso la IA (un supuesto por renglón en `motivos`) y los supuestos generales. */
export interface RespuestaEstimarConsumo {
  cargas: Carga[];
  motivos: string[];
  supuestos: string[];
  explicacion: string;
  uso: UsoIA;
  /** ADENDA z52-2 (modo 'aumento'): los kWh DE MÁS por mes calendario ENE…DIC (consumoPorMesDeCargas con la curva de Sonora),
   *  cómo lo dice la propuesta y de dónde salió. Ausentes en el modo 'nuevo'. */
  kwh_mes?: number[];
  motivo?: string;
  fuente?: 'ia' | 'ia_simulada';
}
export interface RespuestaCorregir {
  /** Propuesta de la IA: la interfaz la enseña y el vendedor la aplica con editarBorrador. */
  propuesta: Omit<ResultadoCorregir, 'uso'>;
  uso: UsoIA;
}
/** ADENDA A0b · respuesta de pedirAprobacion: la cotización (con la aprobación pendiente) y el mensaje para Diego.
 *  `whatsapp_url` = https://wa.me/<WhatsApp de Diego>?text=… (null si ningún director activo tiene WhatsApp: la
 *  interfaz enseña `texto` para copiarlo). El texto NO lleva datos privados del cliente. */
export interface RespuestaAprobacion {
  cotizacion: Cotizacion;
  /** WhatsApp a Diego (null si no tiene número). */
  whatsapp_url: string | null;
  texto: string;
  /** A quién se le puede pedir (Diego, 11-oct: "que el botón deje elegir"): Diego y, si es solo un descuento, Miguel y Juan
   *  Carlos (asesores internos activos con WhatsApp que no son quien pide ni el vendedor). */
  whatsapp_opciones?: { nombre: string; url: string }[];
}
export interface RespuestaVersion {
  version: ResumenVersion;
  cotizacion: Cotizacion;
  /** Solo el PDF de todas las sucursales (regla «y» de Diego, 13-oct): las que quedaron FUERA del paquete, cada una con su
   *  porqué y qué hacer («sácale su PDF individual»). Ausente = entraron todas. */
  fuera_del_pdf?: string[];
  /** Solo el PDF de todas (verificador del 2-nov, z68-1): las ciudades donde salió cobrando el viaje de cada sucursal porque una trae precio a mano; cuál, y a dónde ir a
   *  arreglarlo. No se guarda. Ausente = se cobró un viaje por ciudad donde tocaba. */
  viaje_sin_aplicar?: string[];
}
export interface RespuestaVerVersion {
  version: VersionCotizacion;
}
export interface RespuestaPrecios {
  /** La tabla COMPLETA (con costos y piso): solo para quien tiene `ver_costos` (el director). null para los demás. */
  tabla: TablaPrecios | null;
  historial: Pick<TablaPrecios, 'version' | 'vigente_desde' | 'editada_por' | 'nota'>[];
  /** Lo que ve cualquiera: precios por tamaño, tope de descuento sin OK y el catálogo de adicionales con su precio
   *  sugerido (sin costos). La usa el formulario de costos adicionales. */
  vista: VistaPrecios;
}
export type RespuestaEstadoIA = EstadoIA;
/** Contrato v2 · RUTAS_PREPARADAS.verBitacora (más reciente primero). */
export interface RespuestaBitacora {
  entradas: EntradaBitacora[];
}
